import React from "react";
import { ArrowCounterClockwise, CheckCircle, WarningOctagon, X, CloudSlash, CloudCheck, CloudArrowUp } from "@phosphor-icons/react";
import { useScorekeeper } from "@/hooks/useScorekeeper";
import { TeamPanel } from "./scorekeeper/TeamPanel";
import { FinalizeDialog } from "./scorekeeper/FinalizeDialog";
import { EmergencyPdfMenu } from "./scorekeeper/EmergencyPdfMenu";

export function GameScorekeeper({ gameId, onFinalized }) {
  const sk = useScorekeeper(gameId, onFinalized);
  const {
    game, loaded, home, away, homePlayer, awayPlayer, setHomePlayer, setAwayPlayer,
    saving, autoSave, setAutoSave, saveStatus, finalized, finalizing, setFinalizing,
    history, adds, teamOf, playerById, undo, saveProgress, foulOut, setFoulOut,
    online, pendingSync, syncPending, conflict, forceSync, discardLocal,
  } = sk;

  if (loaded && !game) {
    return <div className="text-center py-32 text-white/50" data-testid="scorekeeper-game-missing">This game could not be found. It may have been deleted.</div>;
  }
  if (!game) {
    return <div className="text-center py-32 text-white/40">Loading game…</div>;
  }
  if (finalized) {
    return (
      <div className="text-center py-32" data-testid="scorekeeper-finalized">
        <CheckCircle size={80} weight="fill" className="text-[#00E676] mx-auto mb-6" />
        <h2 className="font-display text-5xl uppercase">Game Finalized</h2>
        <p className="text-white/60 mt-3">Final: {teamOf(game.home_team_id)?.name} {home} — {away} {teamOf(game.away_team_id)?.name}. Box scores locked; standings, leaderboards & player profiles updated.</p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <EmergencyPdfMenu sk={sk} />
          <button onClick={() => window.close()} data-testid="scorekeeper-close-tab" className="bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide px-8 py-3.5 transition-all duration-200">Close Tab</button>
        </div>
      </div>
    );
  }

  const statPlayers = Object.keys(adds).map((pid) => playerById(pid)).filter(Boolean);

  const ConnBadge = () => {
    if (!online) return (
      <span data-testid="sk-conn-offline" className="flex items-center gap-1.5 border border-[#FFB300]/50 bg-[#FFB300]/10 text-[#FFB300] text-xs font-bold uppercase tracking-wide px-3 py-1.5">
        <CloudSlash size={16} weight="fill" /> Offline — saved on device
      </span>
    );
    if (pendingSync) return (
      <button data-testid="sk-conn-sync" onClick={() => syncPending()} title="Sync now"
        className="flex items-center gap-1.5 border border-[#FF5722]/50 bg-[#FF5722]/10 text-[#FF5722] text-xs font-bold uppercase tracking-wide px-3 py-1.5 hover:bg-[#FF5722]/20 transition-colors">
        <CloudArrowUp size={16} weight="fill" /> Sync pending — tap to sync
      </button>
    );
    return (
      <span data-testid="sk-conn-online" className="flex items-center gap-1.5 border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676] text-xs font-bold uppercase tracking-wide px-3 py-1.5">
        <CloudCheck size={16} weight="fill" /> Online — synced
      </span>
    );
  };

  return (
    <div data-testid="scorekeeper-game">
      <div className="mb-4 flex items-center justify-between gap-3 flex-wrap" data-testid="sk-conn-bar">
        <ConnBadge />
        <EmergencyPdfMenu sk={sk} />
      </div>
      <p className="text-white/40 text-xs mb-4">Scoring works fully offline. Every tap is saved on this device and syncs automatically when you're back online. Tap <span className="text-[#FFB300] font-semibold">Emergency PDF</span> for a printable paper backup that works even with no internet.</p>
      {conflict && (
        <div data-testid="sk-conflict-banner" className="mb-4 border-2 border-[#FFB300] bg-[#FFB300]/10 px-5 py-4">
          <div className="flex items-start gap-3">
            <WarningOctagon size={26} weight="fill" className="text-[#FFB300] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-xl uppercase text-[#FFB300] leading-none">Sync Conflict</div>
              <p className="text-white/80 text-sm mt-1">
                This game was changed on the server ({conflict.server?.home_score ?? 0}–{conflict.server?.away_score ?? 0}, {(conflict.server?.stats || []).length} stat lines) while you were offline. Choose which version to keep.
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <button onClick={forceSync} data-testid="sk-conflict-overwrite" className="bg-[#FF5722] hover:bg-[#FF7043] text-black font-bold uppercase tracking-wide text-xs px-4 py-2 transition-colors">Keep my offline version</button>
                <button onClick={discardLocal} data-testid="sk-conflict-discard" className="border border-white/25 hover:border-white text-white/80 font-bold uppercase tracking-wide text-xs px-4 py-2 transition-colors">Discard mine &amp; load server</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {foulOut && (() => {
        const ejected = foulOut.type === "tech";
        return (
          <div data-testid={ejected ? "ejection-banner" : "foul-out-banner"} key={foulOut.ts} className="mb-4 flex items-center gap-3 border-2 border-[#FF3B30] bg-[#FF3B30]/15 px-5 py-4 animate-foul-out">
            <WarningOctagon size={30} weight="fill" className="text-[#FF3B30] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-display text-2xl sm:text-3xl uppercase text-[#FF3B30] leading-none">{ejected ? "Ejected" : "Fouled Out"}</div>
              <div className="text-white/85 text-sm sm:text-base mt-1"><span className="font-bold uppercase">{foulOut.name}</span> {ejected ? `was ejected (${foulOut.count} technicals)` : `fouled out (${foulOut.count})`} and has been benched.</div>
            </div>
            <button onClick={() => setFoulOut(null)} data-testid="foul-out-dismiss" className="text-white/50 hover:text-white shrink-0"><X size={22} /></button>
          </div>
        );
      })()}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        <TeamPanel sk={sk} side="home" teamId={game.home_team_id} score={home} player={homePlayer} setPlayer={setHomePlayer} />
        <TeamPanel sk={sk} side="away" teamId={game.away_team_id} score={away} player={awayPlayer} setPlayer={setAwayPlayer} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-5">
        <button onClick={undo} disabled={history.length === 0} data-testid="scorekeeper-undo" className="flex items-center gap-2 border border-white/15 hover:border-white text-white/80 disabled:opacity-40 font-bold uppercase tracking-wide px-5 py-3.5 transition-all duration-200">
          <ArrowCounterClockwise size={20} /> Undo
        </button>
        <button onClick={() => saveProgress(false)} disabled={saving} data-testid="scorekeeper-save" className="border border-[#FF5722] text-[#FF5722] hover:bg-[#FF5722]/10 disabled:opacity-50 font-bold uppercase tracking-wide px-6 py-3.5 transition-all duration-200">
          {saving ? "Saving..." : "Save Progress"}
        </button>
        <label data-testid="autosave-toggle" className="flex items-center gap-2 cursor-pointer select-none border border-white/15 px-4 py-3.5 hover:border-white/40 transition-all duration-200">
          <input type="checkbox" checked={autoSave} onChange={(e) => setAutoSave(e.target.checked)} className="w-4 h-4 accent-[#FF5722]" data-testid="autosave-checkbox" />
          <span className="text-sm font-bold uppercase tracking-wide text-white/70">Auto-save</span>
        </label>
        {autoSave && (
          <span data-testid="autosave-status" className="text-sm uppercase tracking-wide text-white/40">
            {saveStatus === "saving" ? "Saving…" : saveStatus === "saved" ? "Auto-saved ✓" : ""}
          </span>
        )}
        <button onClick={() => setFinalizing(true)} disabled={saving} data-testid="scorekeeper-finish" className="bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide px-8 py-3.5 transition-all duration-200 ml-auto">
          Finalize Game
        </button>
      </div>
      <p className="text-white/40 text-sm mt-3">Tap a player, then log made shots, rebounds, assists, steals, blocks and fouls. Team fouls update automatically from player fouls. Auto-save stores box-score progress. Finalizing locks the box score and updates standings, player profiles, leaderboards & the MVP race.</p>

      {finalizing && <FinalizeDialog sk={sk} statPlayers={statPlayers} />}
    </div>
  );
}
